import type { Json, ViewDelta } from '@w/crdt'
import { classifyBlockDelta, type PageBlock } from '@w/content-blocks-editor/doc-bindings-for-page'

/**
 * Blocks whose membership, position or own fields feed effects outside the page's content row, which only the device
 * transaction writes (page-collaboration-live-patches.design.md "Structural edits live-owned", brainstorm Q1(b)):
 * questions → the author's personal cards and retained parts (card-derivation.ts:77-147), media → bucket calls
 * (commit-page-edit.ts:228-262), page blocks → child page rows (commit-page-edit.ts:221-227).
 */
const deviceTypes: ReadonlySet<string> = new Set(['question', 'cloze', 'rule', 'img', 'audio', 'video', 'file', 'page'])

/**
 * Whether the room may publish this change: everything outside page content stays as is, and no device-owned block is
 * inserted, removed, moved, retyped or has an own field changed. Text and formats inside any block, and every other
 * block's structure (paragraphs, lists, page links), are room-published. One pure function on the view delta, so the
 * author (page.tsx) and the room (live-rooms.ts) decide the same frame the same way.
 */
export function isRoomPublishable(delta: ViewDelta): boolean {
  if (!sameOutsideChildren(delta.before, delta.after)) return false
  const changes = classifyBlockDelta(delta)
  if ([...changes.inserted, ...changes.deleted].some(location => holdsDeviceBlock(location.block))) return false
  if (changes.moved.some(change => holdsDeviceBlock(change.after.block) || holdsDeviceBlock(change.before.block))) return false
  return changes.updated.every(({ before, after }) => {
    const device = deviceTypes.has(before.block.type) || deviceTypes.has(after.block.type) || 'qid' in before.block || 'qid' in after.block
    return !device || sameOwnFields(before.block, after.block)
  })
}

/** The page root and `content` may change only inside `content.children` (title, entry and scalars are device fields). */
function sameOutsideChildren(before: Json, after: Json): boolean {
  if (before === after) return true
  const old = record(before)
  const next = record(after)
  if (!old || !next || !sameKeys(old, next, key => key === 'content')) return false
  const oldContent = record(old.content)
  const nextContent = record(next.content)
  return Boolean(oldContent && nextContent && sameKeys(oldContent, nextContent, key => key === 'children'))
}

function sameKeys(b: Readonly<Record<string, Json>>, a: Readonly<Record<string, Json>>, skip: (key: string) => boolean) {
  const keys = Object.keys(b)
  return keys.length === Object.keys(a).length && keys.every(key => Object.hasOwn(a, key) && (skip(key) || equal(b[key], a[key])))
}

/** A block or any block inside it is device-owned. */
function holdsDeviceBlock(value: unknown): boolean {
  if (Array.isArray(value)) return value.some(item => holdsDeviceBlock(item))
  if (!value || typeof value !== 'object') return false
  const node = value as Record<string, unknown>
  if (typeof node.type === 'string' && (deviceTypes.has(node.type) || 'qid' in node)) return true
  return Object.values(node).some(item => holdsDeviceBlock(item))
}

/** Equal fields of the block itself: nested blocks count only by their id and order, their own changes are classified apart. */
function sameOwnFields(before: PageBlock, after: PageBlock): boolean {
  return equal(own(before), own(after))
}

function own(value: unknown, top = true): unknown {
  if (Array.isArray(value)) return value.map(item => own(item, false))
  if (!value || typeof value !== 'object') return value
  const node = value as Record<string, unknown>
  if (!top && typeof node.id === 'string' && typeof node.type === 'string') return { block: node.id }
  return Object.fromEntries(Object.entries(node).map(([key, item]) => [key, own(item, false)]))
}

function record(value: Json | undefined): Readonly<Record<string, Json>> | undefined {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : undefined
}

function equal(left: unknown, right: unknown): boolean {
  if (left === right) return true
  if (!left || !right || typeof left !== 'object' || typeof right !== 'object' || Array.isArray(left) !== Array.isArray(right)) return false
  const a = left as Record<string, unknown>, b = right as Record<string, unknown>
  const keys = Object.keys(a)
  return keys.length === Object.keys(b).length && keys.every(key => Object.hasOwn(b, key) && equal(a[key], b[key]))
}
